import {useTranslation} from "react-i18next";
import {useAxios} from "../../../shared/technical/rest/AxiosProvider";
import {MutationOptions, useMutation, useQuery, useQueryClient} from "react-query";
import React, {useCallback, useMemo, useState} from "react";
import {ApplicationUserGroup, CreateApplicationUserGroupMembership} from "./ApplicationUserGroup";
import {
  addMemberToApplicationUserGroup,
  deleteMemberOfApplicationUserGroup,
  getAllApplicationUserGroupMembers
} from "./ApplicationUserGroupApi";
import {ContentPageTableHeader} from "../../../shared/components/tables/ContentPageTableHeader";
import {Grid} from "@mui/material";
import {ContentPageTable} from "../../../shared/components/tables/ContentPageTable";
import {Application} from "../Application";
import {applicationUserGroupMemberColumns} from "./ApplicationUserGroupMemberColumns";
import {FormDialog} from "../../../shared/components/dialogs/FormDialog";
import {AddApplicationUserGroupMembershipForm} from "./AddApplicationUserGroupMembershipForm";
import {useAccount} from "../../users/AccountProvider";
import {mayEditApplication} from "../permissions";
import {EntityRef} from "../../../shared/technical/rest/entities";

export function ApplicationUserGroupMembershipForm(props: {
  application: Application,
  applicationUserGroup: ApplicationUserGroup,
  readonly: boolean
}) {
  const {application, applicationUserGroup} = props;
  const {t} = useTranslation();
  const axios = useAxios();
  const account = useAccount();
  const queryClient = useQueryClient();
  const [showFormDialog, setShowFormDialog] = useState(false);

  const mutateOptions: MutationOptions<any, any, any> = useMemo(() => ({
    onSuccess: () => {
      return queryClient.invalidateQueries('applicationUserGroupMemberships').then(() => queryClient.invalidateQueries('applicationUserGroups'));
    }
  }), [queryClient]);

  const {data} = useQuery('applicationUserGroupMemberships', () => getAllApplicationUserGroupMembers(axios, application.id!!, applicationUserGroup.id!!), {});
  const addMemberToApplicationUserGroupMutation = useMutation((userGroupMember: CreateApplicationUserGroupMembership) =>
    addMemberToApplicationUserGroup(axios, application.id!!, applicationUserGroup.id!!, userGroupMember), mutateOptions);
  const deleteMemberOfApplicationUserGroupMutation = useMutation((membershipId: string) =>
    deleteMemberOfApplicationUserGroup(axios, application.id!!, applicationUserGroup.id!!, membershipId), mutateOptions);

  const handleAddButtonClick = useCallback(() => {
    setShowFormDialog(true);
  }, [setShowFormDialog]);

  const handleAddMembers = useCallback((users: EntityRef[]) => {
    users.forEach((user) => addMemberToApplicationUserGroupMutation.mutate({user: user}));
    setShowFormDialog(false);
  }, [addMemberToApplicationUserGroupMutation, setShowFormDialog]);

  const handleDeleteMember = useCallback((membershipId: string) => {
    deleteMemberOfApplicationUserGroupMutation.mutate(membershipId);
  }, [deleteMemberOfApplicationUserGroupMutation]);

  const handleCloseDialog = useCallback(() => {
    setShowFormDialog(false);
  }, [setShowFormDialog]);

  return (
    <>
      <ContentPageTableHeader onClick={handleAddButtonClick}
                              title={t('ApplicationUserGroupMemberships.titleLong')}
                              titleButton={t('ApplicationUserGroupMemberships.buttonCreate')}
                              isEditable={mayEditApplication(account, application)}/>
      <Grid container={true} sx={{marginTop: 1}} spacing={2}>
        {data &&
          <Grid item={true} xs={12}>
            <ContentPageTable entities={data}
                              columns={applicationUserGroupMemberColumns(t, handleDeleteMember, !mayEditApplication(account, application))}
                              tableGridSize={{}} tableOptions={{filter: false}}/>
          </Grid>}
      </Grid>
      <FormDialog open={showFormDialog} handleClose={handleCloseDialog}>
        <AddApplicationUserGroupMembershipForm application={props.application} members={data || []}
                                               handleSave={handleAddMembers} handleClose={handleCloseDialog}/>
      </FormDialog>
    </>
  );
}
